import { createComponentStore } from '../../utils/createComponentStore';

const keyAuto = () => {
  return 'ai_assistant' + (Math.floor(Math.random() * 90000000) + 10000).toString();
};

const defaultAiAssistantData = () => ({
  task: '',
  context: '',
  imageContext: [],
  outputPlaceholders: [
    {
      desc: '',
      key: keyAuto(),
      type: 'text',
    },
  ],
  gptModel: 'gpt-5-mini',
});

const initData = () => ({
  aiAssistantData: defaultAiAssistantData(),
});

// Defensively merge loaded data with defaults so partial/corrupted backend
// payloads (e.g. legacy rows that lost aiAssistantData due to the prior
// race-condition save bug) don't leave the store with data.aiAssistantData
// === undefined, which would crash every setter.
const onLoadData = (rawData) => {
  const base = initData();
  const incoming = rawData && typeof rawData === 'object'
    ? JSON.parse(JSON.stringify(rawData))
    : {};
  const mergedAiAssistantData = {
    ...base.aiAssistantData,
    ...(incoming.aiAssistantData && typeof incoming.aiAssistantData === 'object'
      ? incoming.aiAssistantData
      : {}),
  };
  return { ...incoming, aiAssistantData: mergedAiAssistantData };
};

// Returns a safe, deep-cloned `data` object that is guaranteed to have an
// `aiAssistantData` subtree. Used by every setter so corrupted/missing state
// self-heals instead of throwing.
const ensureData = (cur) => {
  const safe = cur && typeof cur === 'object'
    ? JSON.parse(JSON.stringify(cur))
    : {};
  if (!safe.aiAssistantData || typeof safe.aiAssistantData !== 'object') {
    safe.aiAssistantData = defaultAiAssistantData();
  }
  return safe;
};

const { getStore, defaultStore } = createComponentStore({
  type: 'ai_assistant',
  endpoint: 'ai-assistant',
  registryKey: 'ai_assistant',
  initData,
  onLoadData,

  extraState: () => ({
    placeholders: [],
    position: null,
    focus: null,
    imageContext: [],
  }),

  extraActions: (set, get) => ({
    setPlaceholders: (placeholders) => {
      set({ placeholders: [...placeholders] });
    },

    setAIOutputPlaceholders: (aiOutPutPlaceholders) => {
      const data = ensureData(get().data);
      data.aiAssistantData.outputPlaceholders = aiOutPutPlaceholders;
      set({ data });
    },

    setGptModel: (gptModel) => {
      const data = ensureData(get().data);
      data.aiAssistantData.gptModel = gptModel;
      set({ data });
    },

    setFocus: (index) => {
      set({ placeholders: [], position: index });
    },

    setTask: (task) => {
      const data = ensureData(get().data);
      data.aiAssistantData.task = task;
      set({ data });
    },

    setContext: (context) => {
      const data = ensureData(get().data);
      data.aiAssistantData.context = context;
      set({ data });
    },

    clearImageContext: () => {
      const data = ensureData(get().data);
      data.aiAssistantData.imageContext = [];
      set({ data });
    },

    setImageContext: (imageContext) => {
      const data = ensureData(get().data);
      if (Array.isArray(data.aiAssistantData.imageContext)) {
        if (data.aiAssistantData.imageContext.includes(imageContext)) return;
        data.aiAssistantData.imageContext.push(imageContext);
      } else {
        data.aiAssistantData.imageContext = [imageContext];
      }
      set({ data });
    },

    deleteChip: (index) => {
      const data = ensureData(get().data);
      if (Array.isArray(data.aiAssistantData.imageContext)) {
        data.aiAssistantData.imageContext.splice(index, 1);
      }
      set({ data });
    },

    // Override getTemplate to include extra fields
    getTemplate: () => {
      const { componentId, name, position, data, type, lastModified, placeholders, imageContext } = get();
      return {
        componentId,
        name,
        position,
        data: JSON.parse(JSON.stringify(data)),
        type,
        lastModified,
        placeholders,
        imageContext,
      };
    },

    // Override setTemplateData to deep-clone
    setTemplateData: (data) => {
      set({ data: JSON.parse(JSON.stringify(data)) });
    },

    // Backward-compatible alias
    setAiAssistantVariables: (component) => {
      get().setComponentData(component);
    },
  }),
});

export function getAIAssistantTemplateStore(componentId) {
  return getStore(componentId);
}

export default defaultStore;
